import { useState } from "react";
import { Play, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { YOUTUBE_EMBED, YOUTUBE_THUMB, YOUTUBE_TITLE, YOUTUBE_VERTICAL } from "../data/content";

/**
 * Premium video card with thumbnail + play button. Opens lightbox modal with embedded YouTube.
 */
export const VideoLightbox = ({ className = "" }) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="video-lightbox-trigger"
                className={`group relative block aspect-video w-full overflow-hidden rounded-2xl border border-gold/30 bg-black transition-all duration-500 hover:border-gold hover:shadow-[0_25px_60px_-25px_hsl(var(--gold)/0.5)] ${className}`}
            >
                {/* Thumbnail */}
                <img
                    src={YOUTUBE_THUMB}
                    alt={YOUTUBE_TITLE}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => {
                        // fallback to hqdefault
                        e.currentTarget.src = YOUTUBE_THUMB.replace("maxresdefault", "hqdefault");
                    }}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/30 transition-opacity duration-500 group-hover:from-black/80" />

                {/* Centered play */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                        <span className="absolute inset-0 animate-ping rounded-full border border-gold/60" />
                        <span
                            className="absolute inset-[-8px] rounded-full border border-gold/30"
                        />
                        <div className="relative flex h-20 w-20 items-center justify-center rounded-full border-2 border-gold bg-gold/10 backdrop-blur-md transition-all duration-500 group-hover:scale-110 group-hover:bg-gold">
                            <Play
                                className="h-8 w-8 translate-x-1 text-gold transition-colors duration-500 group-hover:text-black"
                                fill="currentColor"
                            />
                        </div>
                    </div>
                </div>

                {/* Bottom label */}
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 md:p-6">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">
                            Ver en acción
                        </div>
                        <div className="mt-1 font-display text-lg text-white md:text-xl">
                            {YOUTUBE_TITLE}
                        </div>
                    </div>
                    <div className="hidden items-center gap-1.5 rounded-full border border-white/30 bg-black/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white backdrop-blur sm:flex">
                        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                        YouTube
                    </div>
                </div>

                {/* Gold corner accents */}
                <div className="pointer-events-none absolute left-3 top-3 h-6 w-6 border-l-2 border-t-2 border-gold/70" />
                <div className="pointer-events-none absolute right-3 top-3 h-6 w-6 border-r-2 border-t-2 border-gold/70" />
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    data-testid="video-lightbox-modal"
                    className={`border-gold/40 bg-black p-0 sm:rounded-2xl ${
                        YOUTUBE_VERTICAL ? "max-w-xs sm:max-w-sm" : "max-w-5xl"
                    }`}
                >
                    <DialogTitle className="sr-only">{YOUTUBE_TITLE}</DialogTitle>
                    <button
                        onClick={() => setOpen(false)}
                        aria-label="Cerrar video"
                        data-testid="video-lightbox-close"
                        className="absolute -right-2 -top-12 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/50 bg-black text-gold transition hover:scale-105 hover:border-gold sm:-right-0 sm:-top-12"
                    >
                        <X className="h-5 w-5" />
                    </button>
                    <div
                        className={`w-full overflow-hidden rounded-2xl ${
                            YOUTUBE_VERTICAL ? "aspect-[9/16]" : "aspect-video"
                        }`}
                    >
                        {open && (
                            <iframe
                                title={YOUTUBE_TITLE}
                                src={YOUTUBE_EMBED}
                                className="h-full w-full border-0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default VideoLightbox;
